import { ActivityEntityName } from '../shared/activity.entity.name';
import { EntityLink } from '../entity';
import { SaleOrder, SaleOrderStatus } from './sale.order';

/**
 * Create/update allowlist (production contract).
 * Number, status, totals, Invoice lineage, audit, and derived fields are server-owned.
 */
export const SALE_ORDER_MUTABLE_FIELDS = [
  'account',
  'contact',
  'customerRef',
  'billingCompany',
  'date',
  'expiryDate',
  'taxInclusiveOption',
  'sections',
  'theme',
  'priceList',
  'notes',
  'attachments',
  'links',
] as const;

export type SaleOrderMutableField = (typeof SALE_ORDER_MUTABLE_FIELDS)[number];

/** Fields clients must never set via generic save/patch. */
export const SALE_ORDER_PROTECTED_FIELDS = [
  'saleOrderNumber',
  'status',
  'totalEx',
  'totalTax',
  'totalInc',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'active',
] as const;

export type SaleOrderProtectedField = (typeof SALE_ORDER_PROTECTED_FIELDS)[number];

export function listForbiddenSaleOrderPatchFields(patch: Partial<SaleOrder> | Record<string, unknown> | null | undefined): string[] {

  if (!patch) return [];
  return SALE_ORDER_PROTECTED_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(patch, field));
}

export function assertSaleOrderPatchAllowlist(patch: Partial<SaleOrder> | Record<string, unknown> | null | undefined): void {

  const forbidden = listForbiddenSaleOrderPatchFields(patch);
  if (forbidden.length > 0) {
    throw new Error(`Sale order patch rejected protected fields: ${forbidden.join(', ')}`);
  }
}

/** Date-typed fields accepted from clients. Wire format is an ISO string (or Date/null). */
export const SALE_ORDER_DATE_FIELDS = ['date', 'expiryDate'] as const;

/**
 * Coerce ISO strings/numbers to Dates and reject anything uncastable (e.g. a Date instance
 * degraded to `{}` by spread-cloning) so it fails loudly instead of corrupting the write.
 */
export function normalizeSaleOrderDates(patch: Partial<SaleOrder> | null | undefined): Partial<SaleOrder> {

  const result = { ...(patch ?? {}) } as Record<string, unknown>;
  for (const field of SALE_ORDER_DATE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(result, field)) continue;
    const value = result[field];
    if (value === null || value === undefined) continue;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) throw new Error(`${field} is not a valid date`);
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) throw new Error(`${field} is not a valid date`);
      result[field] = parsed;
      continue;
    }
    throw new Error(`${field} is not a valid date value`);
  }
  return result as Partial<SaleOrder>;
}

export function pickSaleOrderMutableFields(source: Partial<SaleOrder> | null | undefined): Partial<SaleOrder> {

  const result: Partial<SaleOrder> = {};
  if (!source) return result;

  for (const field of SALE_ORDER_MUTABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      (result as Record<string, unknown>)[field] = (source as Record<string, unknown>)[field];
    }
  }
  return normalizeSaleOrderDates(result);
}

export function isSaleOrderEditable(status: SaleOrderStatus | undefined | null): boolean {

  return status === SaleOrderStatus.New || status == null;
}

export function assertSaleOrderEditable(status: SaleOrderStatus | undefined | null): void {

  if (!isSaleOrderEditable(status)) {
    throw new Error(`Sale order is not editable in status ${status}`);
  }
}

/** Job-typed links are allowed on create/update; Invoice lineage links are server-owned. */
export function sanitizeSaleOrderClientLinks(links: EntityLink[] | undefined, existing?: EntityLink[]): EntityLink[] {

  const preservedInvoiceLinks = (existing ?? []).filter(link => link?.type === ActivityEntityName.Invoice);
  const clientLinks = (links ?? []).filter(link => link?.type !== ActivityEntityName.Invoice);
  return [...clientLinks, ...preservedInvoiceLinks];
}

export function findInvoiceLink(saleOrder: Pick<SaleOrder, 'links'> | null | undefined, invoiceId: string | undefined | null): EntityLink | undefined {

  if (!invoiceId) return undefined;
  return (saleOrder?.links ?? []).find(link => link?.type === ActivityEntityName.Invoice && link._id === invoiceId);
}

export function hasInvoiceLink(saleOrder: Pick<SaleOrder, 'links'> | null | undefined): boolean {

  return (saleOrder?.links ?? []).some(link => link?.type === ActivityEntityName.Invoice);
}
